import TitleBar from '../../../components/TitleBar';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import { Button } from '../../../components';
import './processAreaMitigation.css';
import { Footer } from '../../../components/common/Footer';
import { FETCH_MITIGATION_DASHBOARD } from '../queries/processMitigation';
import { useQuery } from '@apollo/client';
import FinancialData from '../../../components/common/FinancialData';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import QueryErrorBoundary from '../../../components/error-handling';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import TitleUpdater from '../../../components/common/TitleUpdater';

type ProcessAreaAndProcessMitigationDashboard = {
  residualRevenueExposure: number;
  externalMitigation: number;
  finishedGoods: number;
  internalMitigation: number;
  onsiteMitigation: number;
  children: ProcessAreaAndProcessMitigationDashboard[];
  name: string;
  key: string;
  replacementTime: number;
  totalMitigation: number;
  totalRevenueExposure: number;
  __typename: string;
};

type DataReturned = {
  processAreaAndProcessMitigationDashboard: ProcessAreaAndProcessMitigationDashboard[];
};

export function PALocation() {
  const params = useParams();
  const locationId = params.locationId!;
  let [searchParams] = useSearchParams();
  const locationName = searchParams.get('location')!;
  const navigate = useNavigate();

  const { data, loading, error } = useQuery<any>(FETCH_MITIGATION_DASHBOARD, {
    variables: { uuid: locationId },
    skip: !locationId,
  });

  const columns: ColumnsType<any> = [
    {
      title: 'Process Area/Process',
      dataIndex: 'processArea',
      key: 'processArea',
      width: 200,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(
          a.processArea || a.process,
          b.processArea || b.process,
        ),
      render: (text, record) => {
        return (
          <span className="custom-table--column">
            {text || record?.process}
          </span>
        );
      },
    },
    {
      title: (
        <div>
          Replacement Time <br />
          (months)
        </div>
      ),
      dataIndex: 'replacementTime',
      key: 'replacementTime',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.replacementTime, b.replacementTime),
      render: (text) => {
        return <span className="custom-table--column">{text}</span>;
      },
    },
    {
      title: 'Total Revenue Exposure',
      dataIndex: 'totalRevenueExposure',
      key: 'totalRevenueExposure',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.totalRevenueExposure,
          b.totalRevenueExposure,
        ),
      render: (text) => {
        return (
          <span className="custom-table--column">
            {<FinancialData text={text} />}
          </span>
        );
      },
    },
    {
      title: 'Finished Goods',
      dataIndex: 'finishedGoods',
      key: 'finishedGoods',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.finishedGoods, b.finishedGoods),
      render: (text) => {
        return (
          <span className="custom-table--column">
            {<FinancialData text={text} />}
          </span>
        );
      },
    },
    {
      title: 'Onsite Mitigation',
      dataIndex: 'onsiteMitigation',
      key: 'onsiteMitigation',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.onsiteMitigation, b.onsiteMitigation),
      render: (text) => {
        return (
          <span className="custom-table--column">
            {<FinancialData text={text} />}
          </span>
        );
      },
    },
    {
      title: 'Internal Mitigation',
      dataIndex: 'internalMitigation',
      key: 'internalMitigation',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.internalMitigation, b.internalMitigation),
      render: (text) => {
        return (
          <span className="custom-table--column">
            {<FinancialData text={text} />}
          </span>
        );
      },
    },
    {
      title: 'External Mitigation',
      dataIndex: 'externalMitigation',
      key: 'externalMitigation',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.externalMitigation, b.externalMitigation),
      render: (text) => {
        return (
          <span className="custom-table--column">
            {<FinancialData text={text} />}
          </span>
        );
      },
    },
    {
      title: 'Total Mitigation',
      dataIndex: 'totalMitigation',
      key: 'totalMitigation',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.totalMitigation, b.totalMitigation),
      render: (text) => {
        return (
          <span className="custom-table--column">
            {<FinancialData text={text} />}
          </span>
        );
      },
    },
    {
      title: 'Residual Revenue Exposure',
      dataIndex: 'residualRevenueExposure',
      key: 'residualRevenueExposure',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.residualRevenueExposure,
          b.residualRevenueExposure,
        ),
      render: (text) => {
        return (
          <span className="custom-table--column">
            {<FinancialData text={text} />}
          </span>
        );
      },
    },
  ];

  const extendActions = (record: any) => {
    const IS_PROCESS_AREA = record.__typename === 'MitigationDashboardModel';

    const path = IS_PROCESS_AREA
      ? 'process-area-mitigation'
      : 'process-mitigation';

    const uuid = IS_PROCESS_AREA ? record.processAreaUUID : record.processUUID;

    const name = IS_PROCESS_AREA ? record.processArea : record.process;

    return (
      <Button
        type="primary-rounded"
        className="text-sm min-w-[140px]"
        onClick={() =>
          navigate(
            `/mitigation/${locationId}/${path}/${uuid}?location=${locationName}&processArea=${name}`,
          )
        }
      >
        Update Mitigation
      </Button>
    );
  };

  const baseColumns: ExportColumn[] = [
    {
      key: 'name',
      header: 'Process Area/Process',
      type: 'text',
      width: 45,
    },
    {
      key: 'replacementTime',
      header: 'Replacement Time (months)',
      type: 'number',
      width: 30,
    },
    {
      key: 'totalRevenueExposure',
      header: 'Total Revenue Exposure',
      type: 'money',
      width: 30,
    },
    {
      key: 'finishedGoods',
      header: 'Finished Goods',
      type: 'money',
      width: 30,
    },
    {
      key: 'onsiteMitigation',
      header: 'Onsite Mitigation',
      type: 'money',
      width: 30,
    },
    {
      key: 'internalMitigation',
      header: 'Internal Mitigation',
      type: 'money',
      width: 30,
    },
    {
      key: 'externalMitigation',
      header: 'External Mitigation',
      type: 'money',
      width: 30,
    },
    {
      key: 'totalMitigation',
      header: 'Total Mitigation',
      type: 'money',
      width: 30,
    },
    {
      key: 'residualRevenueExposure',
      header: 'Residual Revenue Exposure',
      type: 'money',
      width: 30,
    },
  ];

  const normalizedNamesData = (
    data?.processAreaAndProcessMitigationDashboard || []
  )?.map((item: any) => {
    return {
      ...item,
      name: item.processArea,
      procesess: (item.procesess || [])?.map((process: any) => {
        return {
          ...process,
          name: process.process,
        };
      }),
    };
  });

  return (
    <div>
      <TitleUpdater title={`${locationName}/Process Areas & Processes`} />
      <TitleBar
        title="Process Area & Process Mitigation"
        brief="Add mitigation for any of the process areas and processes at this location. If a process area or process is missing, you can add it in the data inputs section."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title={`${locationName} - Process Areas`}
          columns={columns}
          data={normalizedNamesData || []}
          loading={loading}
          extendActions={extendActions}
          customStyles="custom-column-PA--heading custom-column-PA--revenue"
          rowKey="processAreaUUID"
          childrenColumnName="procesess"
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer goBack />
    </div>
  );
}
